import { now } from '@gilbarbara/helpers';
import produce from 'immer';
import { Reducer } from 'redux';
import layout from '../scss/layout.module.scss';
import _ from 'lodash';

import { ActionCreator, ActionsMapReducer, GenericFunction, StoreAction } from '../types';
import {LodashParseInt} from "lodash/fp";

/**
 * Create an action
 */
export function createAction<T extends GenericFunction>(
  type: string,
  payloadCreator: T,
): ActionCreator<Parameters<T>, ReturnType<T>> {
  if (!payloadCreator) {
    throw new TypeError('Expected a function');
  }

  return (...args: any[]) => ({
    type,
    payload: payloadCreator(...args),
  });
}

/**
 * Create a reducer
 */
export function createReducer<State>(
  actionsMap: ActionsMapReducer<State>,
  defaultState: State,
): Reducer<State, StoreAction> {
  return (state = defaultState, action: StoreAction) =>
    produce(state, (draft: State) => {
      const fn = actionsMap[action.type];

      if (fn) {
        return fn(draft, action);
      }

      return draft;
    });
}

/**
 * Check if cache is valid
 */
export function hasValidCache(lastUpdated: number, max = 10): boolean {
  if (!navigator.onLine) {
    return true;
  }

  return lastUpdated + max * 60 > now();
}

export const mediaSize = {
    xs: `(max-width: ${layout.xsMax})`,
    sm: `(min-width: ${layout.sm}) and (max-width: ${layout.smMax})`,
    md: `(min-width: ${layout.md}) and (max-width: ${layout.mdMax})`,
    lg: `(min-width: ${layout.lg}) and (max-width: ${layout.lgMax})`,
    xl: `(min-width: ${layout.xl}) and (max-width: ${layout.xlMax})`,
    xxl: `(min-width: ${layout.xxl})`
};

export const getSize = (width: number) => {
    if(width < _.parseInt(layout.xsMax)){
        return 'xs'
    }else if(width >= _.parseInt(layout.sm) && width < _.parseInt(layout.smMax)){
        return 'sm'
    }else if(width >= _.parseInt(layout.md) && width < _.parseInt(layout.mdMax)){
        return 'md'
    }else if(width >= _.parseInt(layout.lg) && width < _.parseInt(layout.lgMax)){
        return 'lg'
    }else if(width >= _.parseInt(layout.xl) && width < _.parseInt(layout.xlMax)){
        return 'xl'
    }else{
        return 'xxl'
    }
};

export const isMobile = (size: string) => {
    return ['xs', 'sm'].indexOf(size) > -1
};
export const isTablet = (size: string) => {
    return ['md', 'lg'].indexOf(size) > -1
};

export const navMapFactory = (lang: any, intl: any) => {
    return [
        // {
        //     id: "news",
        //     name: intl.formatMessage({ id: "news" }),
        //     link: `${process.env.PUBLIC_URL}/News?lang=${lang}`
        // },
        {
            id: "AboutIDRF",
            name: intl.formatMessage({ id: "AboutIDRF" }),
            link: `${process.env.PUBLIC_URL}/About?lang=${lang}`,
            children: [
                {
                    id: "ObjectiveOfIDRF",
                    name: intl.formatMessage({ id: "ObjectiveOfIDRF" }),
                    link: `${process.env.PUBLIC_URL}/About?lang=${lang}#Objective`
                },
                {
                    id: "SurveyTarget",
                    name: intl.formatMessage({ id: "SurveyTarget" }),
                    link: `${process.env.PUBLIC_URL}/About?lang=${lang}#Target`
                },
                {
                    id: "DataCollectionPeriod",
                    name: intl.formatMessage({ id: "DataCollectionPeriod" }),
                    link: `${process.env.PUBLIC_URL}/About?lang=${lang}#DataCollectionPeriod`
                },
                {
                    id: "DataCollectionTypesAndMeans",
                    name: intl.formatMessage({ id: "DataCollectionTypesAndMeans" }),
                    link: `${process.env.PUBLIC_URL}/About?lang=${lang}#DataCollectionTypesAndMeans`
                },
                {
                    id: "Legislation",
                    name: intl.formatMessage({ id: "Legislation" }),
                    link: `${process.env.PUBLIC_URL}/About?lang=${lang}#Legislation`
                }
            ]
        },
        {
            id: "NotesForHouseholds",
            name: intl.formatMessage({ id: "NotesForHouseholds" }),
            link: `${process.env.PUBLIC_URL}/Respondent?lang=${lang}`,
            children: [
                {
                    id: "InterviewProcedure",
                    name: intl.formatMessage({ id: "InterviewProcedure" }),
                    link: `${process.env.PUBLIC_URL}/Respondent?lang=${lang}#InterviewProcedure`
                },
                {
                    id: "OnlineCompletion",
                    name: intl.formatMessage({ id: "OnlineCompletion" }),
                    link: `${process.env.PUBLIC_URL}/Respondent?lang=${lang}#OnlineCompletion`
                },
                {
                    id: "MakeAnAppointment",
                    name: intl.formatMessage({ id: "MakeAnAppointment" }),
                    link: `${process.env.PUBLIC_URL}/Respondent?lang=${lang}#MakeAnAppointment`
                },
                {
                    id: "IdentifyAnEnumerator",
                    name: intl.formatMessage({ id: "IdentifyAnEnumerator" }),
                    link: `${process.env.PUBLIC_URL}/Respondent?lang=${lang}#IdentifyAnEnumerator`
                },
                {
                    id: "CooperateReward",
                    name: intl.formatMessage({ id: "CooperateReward" }),
                    link: `${process.env.PUBLIC_URL}/Respondent?lang=${lang}#LuckyDraw`
                },
                {
                    id: "QandA",
                    name: intl.formatMessage({ id: "QandA" }),
                    link: `${process.env.PUBLIC_URL}/Respondent?lang=${lang}#FAQ`
                },
            ]
        },
        {
            id: "Media",
            name: intl.formatMessage({ id: "Media" }),
            link: `${process.env.PUBLIC_URL}/Media?lang=${lang}`,
            children: [
                {
                    id: "InfoGraphics",
                    name: intl.formatMessage({ id: "InfoGraphics" }),
                    link: `${process.env.PUBLIC_URL}/Media?lang=${lang}#InfoGraphic`
                },
                {
                    id: "Videos",
                    name: intl.formatMessage({ id: "Videos" }),
                    link: `${process.env.PUBLIC_URL}/Media?lang=${lang}#Video`
                },
                {
                    id: "Posters",
                    name: intl.formatMessage({ id: "Posters" }),
                    link: `${process.env.PUBLIC_URL}/Media?lang=${lang}#Poster`
                },
                {
                    id: "Radio",
                    name: intl.formatMessage({ id: "Radio" }),
                    link: `${process.env.PUBLIC_URL}/Media?lang=${lang}#Radio`
                }
            ]
        },
        {
            id: "ContactUs",
            name: intl.formatMessage({ id: "ContactUs" }),
            link: `${process.env.PUBLIC_URL}/Contact?lang=${lang}`
        }
    ]
};
